import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '../contexts/user'
import { crossLink, readCheckoutIntent, resumeCheckout, safeRedirect } from '../lib/checkoutIntent'

export default function LoginPage() {
  const { login } = useUser()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // safeRedirect, not the raw param: `//evil.example` reads as a path to
  // navigate() and behaves as another origin.
  const redirectTo = safeRedirect(params.get('redirect'))
  // Set when the visitor pressed Unlock while signed out. They are here to buy
  // something, not to visit an account page.
  const intent = readCheckoutIntent(params)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      await login(email, password)
      if (intent) {
        // Straight to Stripe. Leaving `busy` set on purpose: the browser is
        // about to leave, and re-enabling the button would invite a second press
        // against a checkout session that is already being created.
        await resumeCheckout(intent, redirectTo, { onFallback: navigate })
        return
      }
      navigate(redirectTo)
    } catch (err) {
      setError((err as Error).message || 'Sign in failed')
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full rounded-smooth border border-border-strong bg-white px-4 py-3 font-body text-body text-brand-navy focus:outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20'

  return (
    <>
      <div className="min-h-screen bg-brand-cream flex items-start justify-center px-4 py-16 md:py-24">
        <div className="w-full max-w-md">
          <h1 className="font-heading text-h2 font-bold text-brand-navy mb-2">Sign in</h1>
          {intent ? (
            <p className="font-body text-body-sm text-ink-muted mb-4">
              Sign in and you go straight to checkout. Nothing is charged until the Stripe page.
            </p>
          ) : null}
          <p className="font-body text-body-sm text-ink-muted mb-8">
            New here?{' '}
            <Link to={crossLink('/signup', params, redirectTo)} className="text-brand-navy font-semibold underline">
              Create an account
            </Link>
          </p>

          <form onSubmit={submit} className="space-y-4 bg-white border border-border-subtle rounded-smooth-lg p-6 md:p-8">
            <div>
              <label className="block font-heading text-overline uppercase tracking-wider text-ink-soft mb-1.5">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" className={inputCls} />
            </div>
            <div>
              <label className="block font-heading text-overline uppercase tracking-wider text-ink-soft mb-1.5">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className={inputCls} />
            </div>
            {error ? <p className="font-body text-body-sm text-red-600">{error}</p> : null}
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-brand-navy text-white font-heading text-cta font-bold px-6 py-3.5 rounded-smooth hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {busy ? 'Signing in…' : intent ? 'Sign in and continue' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </>
  )
}
